<template>
  <article
    v-if="tile"
    class="head-to-head h2h-summary head-to-head--style-4"
    @pointerenter="queueHoverAnimation"
    @pointerleave="cancelHoverAnimation"
  >
    <header class="h2h-summary__header">
      <div>
        <router-link :to="marketLink">{{ tile.title }}</router-link>
      </div>
      <span v-if="tile.date">{{ tile.date }}</span>
    </header>
    <h3 class="h2h-summary__headline">
      <template v-if="summary.leader"
        ><span class="h2h-summary__contract-color" :style="contractStyle(summary.leader)">{{
          summary.leader.name
        }}</span>
        {{ summary.verb }}<br />{{ summary.trailer.name }} by {{ summary.difference }}</template
      >
      <template v-else-if="summary.tied"
        >{{ tile.left.name }} is tied<br />with {{ tile.right.name }}</template
      >
      <template v-else>Price comparison unavailable</template>
    </h3>
    <div ref="chart" class="h2h-summary__chart">
      <svg
        data-history-chart
        :data-animation-progress="animationProgress"
        viewBox="-3 0 365 120"
        role="img"
        :aria-label="
          hasHistory
            ? `Last 30 days: ${tile.left.name} ${tile.left.display}; ${tile.right.name} ${tile.right.display}`
            : 'Not enough price history'
        "
      >
        <template v-if="hasHistory">
          <defs>
            <clipPath :id="chartClipId">
              <rect x="-3" y="-10" :width="300 * animationProgress + 3" height="150" />
            </clipPath>
          </defs>
          <line x1="0" :y1="y(50)" x2="300" :y2="y(50)" class="h2h-summary__reference" />
          <g
            v-for="(series, index) in summary.series"
            :key="series.id"
            class="h2h-summary__contract-color"
            :style="contractStyle(series)"
          >
            <path
              v-if="series.hasHistory"
              :data-series="series.id"
              :clip-path="`url(#${chartClipId})`"
              :d="chartPath(series)"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linejoin="round"
            />
            <template v-if="markers[index]">
              <circle
                :cx="markers[index].x"
                :cy="y(markers[index].price)"
                r="5.5"
                fill="currentColor"
              />
              <line
                v-if="endLabelY[index] !== y(markers[index].price)"
                :x1="markers[index].x + 5"
                :y1="y(markers[index].price)"
                :x2="markers[index].x + 10"
                :y2="endLabelY[index]"
                stroke="currentColor"
              />
              <text
                x="362"
                text-anchor="end"
                :y="endLabelY[index] + 5"
                fill="currentColor"
                class="h2h-summary__end-price"
              >
                {{ `${Math.round(markers[index].price)}${tile.currentPriceSymbol}` }}
              </text>
            </template>
          </g>
        </template>
        <text
          v-else
          x="179.5"
          y="60"
          text-anchor="middle"
          dominant-baseline="middle"
          class="h2h-summary__empty"
        >
          Not enough price history
        </text>
      </svg>
      <div class="h2h-summary__axis"><span>30 days ago</span><span>Today</span></div>
    </div>
    <div class="h2h-summary__contracts">
      <div v-for="series in summary.series" :key="series.id" class="h2h-summary__contract">
        <router-link
          class="h2h-summary__identity"
          :to="{ ...marketLink, query: { contractId: series.id } }"
          :aria-label="`${series.name}${series.partyLabel ? ` (${series.partyLabel})` : ''}`"
        >
          <img
            v-if="series.image && !failedImages[series.id]"
            :src="series.image"
            alt=""
            @error="failedImages[series.id] = true"
          />
          <span
            v-else
            class="h2h-summary__initials"
            :style="{ background: series.color, color: series.ink }"
            >{{ initials(series.name) }}</span
          >
          <div
            class="h2h-summary__identity-text"
            :data-name="`${series.name}${series.partyLabel ? ` (${series.partyLabel})` : ''}`"
          >
            <div class="h2h-summary__identity-content">
              <span class="h2h-summary__name"
                >{{ series.name }}{{ series.partyLabel ? ` (${series.partyLabel})` : '' }}</span
              >
              <span
                class="h2h-summary__change"
                :class="{
                  'h2h-summary__change--up': series.weeklyChange > 0,
                  'h2h-summary__change--down': series.weeklyChange < 0,
                  'h2h-summary__change--neutral':
                    series.weeklyChange === null || series.weeklyChange === 0,
                }"
              >
                <span v-if="series.weeklyChange === null || series.weeklyChange === 0"
                  >No change</span
                >
                <template v-else
                  ><market-change-price
                    :price="series.weeklyChange"
                    :symbol="tile.currentPriceSymbol"
                  /><span>this week</span></template
                >
              </span>
            </div>
          </div>
        </router-link>
        <button
          class="h2h-summary__buy h2h-summary__contract-color"
          :style="contractStyle(series)"
          :disabled="isTradeDisabled(series)"
          @click="trade(series, $event)"
        >
          <template v-if="opensOrderSlip"
            >Buy {{ series.buyName
            }}{{ series.buyDisplay ? ` ${series.buyDisplay}` : '' }}</template
          >
          <template v-else>View {{ series.name }}</template>
        </button>
      </div>
    </div>
  </article>
  <div v-else class="head-to-head h2h-summary" role="status">
    <p>Head to Head is unavailable for this market (misconfigured contracts).</p>
  </div>
</template>
<script src="./h2h-summary-with-chart.js"></script>
<style src="./h2h-summary-with-chart.scss" lang="scss"></style>
